import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { getCatalogItems, toggleCatalogItem } from './api'
import {
    AUDIENCE_META, ENABLED_OPTIONS, ENTRY_TYPE_META, KIND_META, KIND_OPTIONS, STATUS_META,
} from './model'
import ItemEditorModal from './ItemEditorModal'

const { Text } = Typography

// 2026-10-22 coder(lq): 能力条目列表；停用即软删（enabled=0），页面不提供物理删除。
export default function ItemPanel() {
    const [keyword, setKeyword] = useState('')
    const [filters, setFilters] = useState({ keyword: '', kind: undefined, enabled: undefined })
    const [query, setQuery] = useState({ page: 1, size: 20 })
    const [data, setData] = useState({ rows: [], total: 0 })
    const [loading, setLoading] = useState(false)
    const [listError, setListError] = useState('')
    const [refresh, setRefresh] = useState(0)
    const [editing, setEditing] = useState(null)
    const [togglingId, setTogglingId] = useState(null)
    const sequence = useRef(0)

    useEffect(() => {
        const id = ++sequence.current
        setLoading(true)
        setListError('')
        getCatalogItems({
            keyword: filters.keyword || undefined,
            kind: filters.kind,
            enabled: filters.enabled,
            page: query.page,
            size: query.size,
        }).then(result => {
            if (id !== sequence.current) return
            const page = result || {}
            setData({ rows: page.rows || [], total: page.total || 0 })
        }).catch(error => {
            if (id !== sequence.current) return
            setData({ rows: [], total: 0 })
            setListError(error && error.message ? error.message : '能力条目加载失败')
        }).finally(() => { if (id === sequence.current) setLoading(false) })
        return () => { sequence.current += 1 }
    }, [filters, query, refresh])

    const applyFilter = patch => {
        setFilters(previous => ({ ...previous, ...patch }))
        setQuery(previous => ({ ...previous, page: 1 }))
    }

    const toggle = row => {
        const nextEnabled = Number(row.enabled) === 1 ? 0 : 1
        const enabling = nextEnabled === 1
        Modal.confirm({
            title: enabling ? `启用「${row.name}」？` : `停用「${row.name}」？`,
            content: enabling
                ? '启用后 CLI 能力目录会重新包含该条目；客户端缓存最多 24 小时内生效，可用 --refresh 立即刷新。'
                : '停用是软删：CLI 能力目录不再返回该条目，库内数据与文档保留，可随时重新启用。',
            okText: enabling ? '启用' : '停用',
            cancelText: '取消',
            onOk: () => {
                setTogglingId(row.id)
                return toggleCatalogItem(row.id, nextEnabled).then(() => {
                    message.success(enabling ? '已启用' : '已停用')
                    setRefresh(value => value + 1)
                }).catch(() => {
                    // api.js 已统一提示，关闭确认框即可，刷新后仍以服务端状态为准。
                }).finally(() => setTogglingId(null))
            },
        })
    }

    const columns = [
        { title: '编码', dataIndex: 'code', width: 180, render: value => <code className="cli-catalog-code">{value}</code> },
        {
            title: '名称', dataIndex: 'name', render: (value, row) => <>
                <div>{value}</div>
                {row.summary ? <Text type="secondary" className="cli-catalog-summary">{row.summary}</Text> : null}
            </>,
        },
        {
            title: '类型', dataIndex: 'kind', width: 90,
            render: value => { const meta = KIND_META[value]; return <Tag color={meta && meta.color}>{meta ? meta.text : value || '-'}</Tag> },
        },
        {
            title: '入口', dataIndex: 'entryValue', width: 240,
            render: (value, row) => {
                if (!value) return '-'
                const label = ENTRY_TYPE_META[row.entryType] || row.entryType || ''
                return <Space size={4} wrap>
                    {label ? <Tag>{label}</Tag> : null}
                    <Text type="secondary" ellipsis={{tooltip: value}} style={{maxWidth: 180}}>{value}</Text>
                </Space>
            },
        },
        {
            title: '受众', dataIndex: 'audience', width: 90,
            render: value => { const meta = AUDIENCE_META[value]; return <Tag color={meta && meta.color}>{meta ? meta.text : value || '-'}</Tag> },
        },
        {
            title: '状态', dataIndex: 'status', width: 160,
            render: (value, row) => {
                const meta = STATUS_META[value]
                const enabled = Number(row.enabled) === 1
                return <Space size={4} wrap>
                    <Tag color={meta && meta.color}>{meta ? meta.text : value || '-'}</Tag>
                    <Tag color={enabled ? 'success' : 'default'}>{enabled ? '启用' : '停用'}</Tag>
                </Space>
            },
        },
        { title: '排序', dataIndex: 'sortOrder', width: 80, align: 'center' },
        { title: '更新时间', dataIndex: 'updateTime', width: 170, render: value => value || '-' },
        {
            title: '操作', width: 140, render: (_, row) => {
                const enabled = Number(row.enabled) === 1
                return <Space size={0}>
                    <Button type="link" size="small" onClick={() => setEditing({ id: row.id })}>编辑</Button>
                    <Button type="link" size="small" danger={enabled} loading={togglingId === row.id}
                        onClick={() => toggle(row)}>{enabled ? '停用' : '启用'}</Button>
                </Space>
            },
        },
    ]

    return <section>
        <Alert type="info" showIcon className="cli-catalog-tip"
            message="能力条目决定 CLI context 输出的场景、工具与入口；停用即软删，不做物理删除，避免目录被误删后 CLI 读到空清单。" />
        <Space wrap className="cli-catalog-toolbar">
            <Input.Search allowClear value={keyword} placeholder="搜索名称 / 编码 / 场景" style={{ width: 260 }}
                onChange={event => setKeyword(event.target.value)}
                onSearch={value => applyFilter({ keyword: (value || '').trim() })} />
            <Select allowClear placeholder="全部类型" style={{ width: 150 }} value={filters.kind}
                options={KIND_OPTIONS} onChange={value => applyFilter({ kind: value })} />
            <Select allowClear placeholder="全部启用状态" style={{ width: 150 }} value={filters.enabled}
                options={ENABLED_OPTIONS} onChange={value => applyFilter({ enabled: value })} />
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => setRefresh(value => value + 1)}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({ id: null })}>新增</Button>
        </Space>
        {listError && <Alert type="error" showIcon message={listError} className="cli-catalog-tip"
            action={<Button size="small" onClick={() => setRefresh(value => value + 1)}>重试</Button>} />}
        <Table rowKey="id" columns={columns} dataSource={data.rows} loading={loading} scroll={{ x: 1320 }}
            pagination={{
                current: query.page,
                pageSize: query.size,
                total: data.total,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100'],
                showTotal: total => `共 ${total} 条能力条目`,
                onChange: (page, size) => setQuery({ page, size }),
            }}
            locale={{ emptyText: listError ? '加载失败，请点击刷新重试' : '暂无能力条目，可点击「新增」录入。' }} />
        <ItemEditorModal open={!!editing} itemId={editing && editing.id}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); setRefresh(value => value + 1) }} />
    </section>
}
